/**
 * @license
 * Copyright TWISS.IO
 * All Rights Reserved.
 * Licensed under the MIT License.
 */

import { Injectable, OnDestroy } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { map } from "rxjs/operators";
import { ApiService } from "@app/@core/services/api";
import { MatDialog } from "@angular/material/dialog";
import { AssociatesClientAddDialogComponent } from "@app/@shared/components/associates/client-add-dialog/client-add-dialog.component";
import { AssociatesClientMergeDialogComponent } from "@app/@shared/components/associates/client-merge-dialog/client-merge-dialog.component";
import { AssociatesLeadAddDialogComponent } from "@app/@shared/components/associates/lead-add-dialog/lead-add-dialog.component";
import { AssociatesLeadMergeDialogComponent } from "@app/@shared/components/associates/lead-merge-dialog/lead-merge-dialog.component";
import { AssociatesNomineeAddDialogComponent } from "@app/@shared/components/associates/nominee-add-dialog/nominee-add-dialog.component";

@Injectable()
export class AssociatesClientRelationshipsService implements OnDestroy {
  constructor(private apiService: ApiService, public dialog: MatDialog) {
    this.initClients();
    this.initSources();
  }

  private subs_getSelections: Subscription;
  public client_sources;
  public nominee_benefit;
  public lead_stages;
  public sales_stages;
  public outcomes;
  public full_clients;
  public clients;
  public leads;

  ngOnDestroy() {
    if (this.subs_getSelections) this.subs_getSelections.unsubscribe();
  }

  initSources() {
    this.subs_getSelections = this.apiService
      .post("selections", {
        lists: ["lfa-client-source", "lfa-lead-stage", "lfa-sales-stage", "lfa-nominee-benefit", "lfa-outcome"],
      })
      .subscribe((res) => {
        // console.log(res);
        let data = res["data"];
        this.client_sources = data["lfa-client-source"];
        this.lead_stages = data["lfa-lead-stage"];
        this.sales_stages = data["lfa-sales-stage"];
        this.nominee_benefit = data["lfa-nominee-benefit"];
        this.outcomes = data["lfa-outcome"];
      });
  }

  initClients() {
    this.getFullClients();
    this.getClients();
    this.getLeads();
  }

  getFullClients(uuid = null) {
    let url = "associates/full-clients";
    url += uuid ? "/" + uuid : "";
    return this.apiService.get(url).pipe(
      map((res) => {
        let data = res["data"];
        if (!uuid) this.full_clients = data;
        return res;
      })
    );
  }

  getClients(uuid = null) {
    let url = "associates/clients";
    url += uuid ? "/" + uuid : "";
    return this.apiService.get(url).pipe(
      map((res) => {
        let data = res["data"];
        if (!uuid) this.clients = data;
        return res;
      })
    );
  }

  updateClient(uuid, data) {
    return this.apiService.patch("associates/clients/" + uuid, data);
  }
  getSubmissions(uuid) {
    return this.apiService.get("associates/clients/" + uuid + "/submissions");
  }
  getPolicies(uuid) {
    return this.apiService.get("associates/clients/" + uuid + "/policies");
  }
  getLogs(uuid) {
    return this.apiService.get("associates/clients/" + uuid + "/logs");
  }
  getIntroducerScheme(uuid) {
    return this.apiService.get("associates/clients/" + uuid + "/introducer-scheme");
  }

  // Create CLient :: Mat-Dialog
  addClientDialog() {
    const dialogRef = this.dialog.open(AssociatesClientAddDialogComponent, {
      width: "600px",
      disableClose: true,
      data: this.client_sources,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result["error"] === false) this.toggleCreatedSwal(result["data"]);
      else this.toggleCreatedSwalClosed();
    });
  }
  // Event :: Client Created
  private _createdSwal = <Subject<boolean>>new Subject();
  createdSwal = this._createdSwal.asObservable();
  toggleCreatedSwal(data) {
    this._createdSwal.next(data);
  }
  // Event :: Client Create Dialog closed
  private _createdSwalClosed = <Subject<boolean>>new Subject();
  createdSwalClosed = this._createdSwalClosed.asObservable();
  toggleCreatedSwalClosed() {
    this._createdSwalClosed.next();
  }

  // Merge Clients :: Mat-Dialog
  mergeClientDialog() {
    const dialogRef = this.dialog.open(AssociatesClientMergeDialogComponent, {
      width: "600px",
      disableClose: true,
      data: this.clients,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result["error"] === false) this.toggleMergedSwal(result["data"]);
    });
  }
  // Event :: Clients Merged
  private _mergedSwal = <Subject<boolean>>new Subject();
  mergedSwal = this._mergedSwal.asObservable();
  toggleMergedSwal(data) {
    this._mergedSwal.next(data);
  }

  getLeads(uuid = null) {
    let url = "associates/leads";
    url += uuid ? "/" + uuid : "";
    return this.apiService.get(url).pipe(
      map((res) => {
        let data = res["data"];
        if (!uuid) this.leads = data;
        return res;
      })
    );
  }
  // Create Lead :: Mat-Dialog
  addLeadDialog() {
    const dialogRef = this.dialog.open(AssociatesLeadAddDialogComponent, {
      width: "600px",
      disableClose: true,
      data: {
        client_sources: this.client_sources,
        lead_stages: this.lead_stages,
        sales_stages: this.sales_stages,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result["error"] === false) this.toggleCreatedLeadSwal(result["data"]);
      // else this.toggleCreatedSwalClosed();
    });
  }
  // Event :: Client Created
  private _createdLeadSwal = <Subject<boolean>>new Subject();
  createdLeadSwal = this._createdLeadSwal.asObservable();
  toggleCreatedLeadSwal(data) {
    this._createdLeadSwal.next(data);
  }

  // Merge Clients :: Mat-Dialog
  mergeLeadDialog() {
    const dialogRef = this.dialog.open(AssociatesLeadMergeDialogComponent, {
      width: "600px",
      disableClose: true,
      data: this.leads,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result["error"] === false) this.toggleMergedLeadSwal(result["data"]);
    });
  }
  // Event :: Clients Merged
  private _mergedLeadSwal = <Subject<boolean>>new Subject();
  mergedLeadSwal = this._mergedLeadSwal.asObservable();
  toggleMergedLeadSwal(data) {
    this._mergedLeadSwal.next(data);
  }

  getNominees(client_uuid) {
    let url = "associates/clients/" + client_uuid + "/nominees";
    return this.apiService.get(url);
  }
  // Create Nominee :: Mat-Dialog
  addNomineeDialog(client_uuid) {
    const dialogRef = this.dialog.open(AssociatesNomineeAddDialogComponent, {
      width: "1200px",
      disableClose: true,
      data: {
        client_uuid: client_uuid,
        nominee_benefit: this.nominee_benefit,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result["error"] === false) this.toggleCreatedNomineeSwal(result["data"]);
    });
  }
  // Event :: Nominee Created
  private _createdNomineeSwal = <Subject<boolean>>new Subject();
  createdNomineeSwal = this._createdNomineeSwal.asObservable();
  toggleCreatedNomineeSwal(data) {
    this._createdNomineeSwal.next(data);
  }
}
